import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	TextField,
	Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Delete } from "@material-ui/icons";
import requests from "../../../Services/HttpServices";
import "./CamereArdentiSelect.css";

const smallProcediButtonStyle = {
	minWidth     : 138,
	height       : 36,
	padding      : "0 16px",
	marginLeft   : 0,
	fontSize     : 14,
	whiteSpace   : "nowrap",
	borderRadius : 2,
};

const closeProcediButtonStyle = {
	minWidth     : 120,
	height       : 42,
	padding      : "0 18px",
	marginLeft   : 0,
	fontSize     : 15,
	borderRadius : 2,
};

const partnerID = (partner) => `${partner?.partnerUserID || ""}`;
const canSearchPartner = (value) => {
	const search = (value || "").trim();
	return /^\d+$/.test(search) ? search.length >= 1 : search.length >= 2;
};
const partnerLabel = (partner) => {
	if (partner?.isMine) return "La mia casa funeraria";
	if (!partner?.name) return "";
	return partner?.partnerID ? `${partner.name} (ID: ${partner.partnerID})` : partner.name;
};

export default function CamereArdentiSelect({ deceasedID }) {
	const [sale, setSale]                                   = useState([]);
	const [selectedSala, setSelectedSala]                   = useState("");
	const [myFuneralHome, setMyFuneralHome]                 = useState(null);
	const [selectedFuneralHome, setSelectedFuneralHome]     = useState(null);
	const [partnerOptions, setPartnerOptions]               = useState([]);
	const [partnerSearch, setPartnerSearch]                 = useState("");
	const [partnerSearching, setPartnerSearching]           = useState(false);
	const [requestStatus, setRequestStatus]                 = useState(null);
	const [loading, setLoading]                             = useState(false);
	const [error, setError]                                 = useState("");
	const [popupOpen, setPopupOpen]                         = useState(false);
	const [configSale, setConfigSale]                       = useState([]);
	const [popupLoading, setPopupLoading]                   = useState(false);
	const [popupSaving, setPopupSaving]                     = useState(false);
	const [popupError, setPopupError]                       = useState("");
	const saleRequestID                                      = useRef(0);

	const isExternal = !!(
		myFuneralHome
		&& selectedFuneralHome
		&& partnerID(myFuneralHome) !== partnerID(selectedFuneralHome)
	);

	const mergePartnerOptions = useCallback((partners) => {
		const merged = [];
		[myFuneralHome, selectedFuneralHome, ...(partners || [])].forEach((partner) => {
			if (!partner || merged.some((item) => partnerID(item) === partnerID(partner))) return;
			merged.push({
				...partner,
				isMine: myFuneralHome && partnerID(partner) === partnerID(myFuneralHome),
			});
		});
		setPartnerOptions(merged);
	}, [myFuneralHome, selectedFuneralHome]);

	const applySaleResponse = useCallback((response) => {
		if (response?.response !== "success") {
			setSale([]);
			setSelectedSala("");
			setError(response?.message || "Camere Ardenti non disponibili");
			return false;
		}

		const myHome = response.data?.myFuneralHome
			? { ...response.data.myFuneralHome, isMine: true }
			: null;
		const selectedHome = response.data?.selectedFuneralHome
			? {
				...response.data.selectedFuneralHome,
				isMine: !!response.data.selectedFuneralHome.isMine,
			}
			: myHome;
		setMyFuneralHome(myHome);
		setSelectedFuneralHome(selectedHome);
		setPartnerSearch(partnerLabel(selectedHome));
		setSale(response.data?.sale || []);
		setSelectedSala(`${response.data?.selectedSala || 0}`);
		setRequestStatus(response.data?.requestStatus || null);
		setError("");
		const initialOptions = [];
		[myHome, selectedHome].forEach((partner) => {
			if (partner && !initialOptions.some((item) => partnerID(item) === partnerID(partner))) {
				initialOptions.push(partner);
			}
		});
		setPartnerOptions(initialOptions);
		return true;
	}, []);

	const loadSale = useCallback((targetPartnerUserID = null, clearSelectedSala = false) => {
		const currentRequestID = saleRequestID.current + 1;
		saleRequestID.current = currentRequestID;
		const query = [];
		if (targetPartnerUserID) query.push(`partnerUserID=${encodeURIComponent(targetPartnerUserID)}`);
		if (deceasedID) query.push(`deceasedID=${encodeURIComponent(deceasedID)}`);
		setLoading(true);
		return requests("get", "camereArdentiSale", query.length ? `?${query.join("&")}` : "")
			.then((result) => {
				if (currentRequestID !== saleRequestID.current) return result;
				const applied = applySaleResponse(result?.data);
				if (applied && clearSelectedSala) setSelectedSala("");
				setLoading(false);
				return result;
			})
			.catch(() => {
				if (currentRequestID !== saleRequestID.current) return null;
				setSale([]);
				setSelectedSala("");
				setError("Camere Ardenti non disponibili");
				setLoading(false);
				return null;
			});
	}, [applySaleResponse, deceasedID]);

	useEffect(() => {
		loadSale();
	}, [loadSale]);

	useEffect(() => {
		const search = partnerSearch.trim();
		if (!canSearchPartner(search) || search === partnerLabel(selectedFuneralHome)) {
			mergePartnerOptions([]);
			setPartnerSearching(false);
			return undefined;
		}

		let active = true;
		const timer = setTimeout(() => {
			setPartnerSearching(true);
			requests("get", "camereArdentiPartners", `?search=${encodeURIComponent(search)}`)
				.then((result) => {
					if (!active) return;
					const partners = result?.data?.data?.partners || [];
					mergePartnerOptions(partners);
					setPartnerSearching(false);
				})
				.catch(() => {
					if (!active) return;
					mergePartnerOptions([]);
					setPartnerSearching(false);
				});
		}, 300);

		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [mergePartnerOptions, partnerSearch, selectedFuneralHome]);

	const selectFuneralHome = (partner) => {
		const nextPartner = partner || myFuneralHome;
		if (!nextPartner) return;
		const nextIsExternal = partnerID(nextPartner) !== partnerID(myFuneralHome);
		setSelectedFuneralHome(nextPartner);
		setPartnerSearch(partnerLabel(nextPartner));
		setSelectedSala(nextIsExternal ? "" : "0");
		setRequestStatus(null);
		loadSale(partnerID(nextPartner), nextIsExternal);
	};

	const handleSalaChange = (event) => {
		const value = event.target.value;
		if (isExternal && `${value}` === "0") {
			selectFuneralHome(myFuneralHome);
			return;
		}
		setSelectedSala(value);
	};

	const applyConfigResponse = (response) => {
		if (response?.response === "success") {
			setConfigSale((response.data?.sale || []).map((sala) => ({
				...sala,
				savedNomeSala: sala.nomeSala || "",
			})));
			setPopupError("");
			return true;
		}
		setPopupError(response?.message || "Configurazione sale non disponibile");
		return false;
	};

	const loadConfig = useCallback(() => {
		setPopupLoading(true);
		requests("get", "camereArdentiConfig", "")
			.then((result) => {
				applyConfigResponse(result?.data);
				setPopupLoading(false);
			})
			.catch(() => {
				setPopupError("Configurazione sale non disponibile");
				setConfigSale([]);
				setPopupLoading(false);
			});
	}, []);

	const openPopup = () => {
		if (isExternal) return;
		setPopupOpen(true);
		loadConfig();
	};

	const updateConfigSalaName = (index, value) => {
		const nextSale = [...configSale];
		nextSale[index] = { ...nextSale[index], nomeSala: value };
		setConfigSale(nextSale);
	};

	const runConfigPost = (url, bodyData) => {
		setPopupSaving(true);
		requests("post", url, bodyData)
			.then((response) => {
				const ok = applyConfigResponse(response);
				if (ok && myFuneralHome) loadSale(partnerID(myFuneralHome));
				setPopupSaving(false);
			})
			.catch(() => {
				setPopupError("Errore durante il salvataggio");
				setPopupSaving(false);
			});
	};

	const renameSala = (sala) => {
		if ((sala.nomeSala || "") === (sala.savedNomeSala || "")) return;
		runConfigPost("camereArdentiRename", { sala: sala.sala, nomeSala: sala.nomeSala || "" });
	};

	const autocompleteOptions = useMemo(() => partnerOptions.map((partner) => ({
		...partner,
		isMine: myFuneralHome && partnerID(partner) === partnerID(myFuneralHome),
	})), [myFuneralHome, partnerOptions]);
	const selectedRoomIsMissing = Number(selectedSala) > 0
		&& !sale.some((sala) => `${sala.sala}` === `${selectedSala}`);

	return (
		<div className="camere-ardenti-section">
			<span className="death-data camere-ardenti-heading">Dati Camere Ardenti</span>
			<Typography variant="body2" className="camere-ardenti-description">
				Se selezioni una sala per questo defunto verrà mostrato sui display delle Camere Ardenti selezionate.
			</Typography>
			<input
				type="hidden"
				name="cameraArdentePartnerUserID"
				value={partnerID(selectedFuneralHome)}
			/>
			<input type="hidden" name="cameraArdenteSala" value={selectedSala} />
			<Autocomplete
				className="camere-ardenti-partner-autocomplete"
				options={autocompleteOptions}
				value={selectedFuneralHome}
				inputValue={partnerSearch}
				loading={partnerSearching}
				loadingText="Ricerca partner..."
				noOptionsText={canSearchPartner(partnerSearch) ? "Nessun partner trovato" : "Digita almeno 2 caratteri o un ID"}
				filterOptions={(options) => options}
				getOptionLabel={partnerLabel}
				getOptionSelected={(option, value) => partnerID(option) === partnerID(value)}
				onInputChange={(event, value) => setPartnerSearch(value)}
				onChange={(event, value, reason) => {
					if (reason === "clear") {
						setPartnerSearch("");
						return;
					}
					if (value) selectFuneralHome(value);
				}}
				disabled={loading && !selectedFuneralHome}
				renderInput={(params) => (
					<TextField
						{...params}
						className="partner-field camere-ardenti-field"
						label="Casa Funeraria"
						fullWidth
					/>
				)}
			/>
			<div className="camere-ardenti-select-row">
				<TextField
					className="partner-field camere-ardenti-field"
					select
					fullWidth
					style={{ flex: 1, textAlign: "left" }}
					disabled={loading}
					label="Sala"
					value={selectedSala}
					required={isExternal}
					onChange={handleSalaChange}
					helperText={error || (loading ? "Caricamento sale..." : "")}
					error={!!error}
					SelectProps={{ SelectDisplayProps: { style: { textAlign: "left" } } }}
				>
					{isExternal && (
						<MenuItem value="" disabled style={{ justifyContent: "flex-start", textAlign: "left" }}>
							Seleziona una Sala
						</MenuItem>
					)}
					{selectedRoomIsMissing && (
						<MenuItem value={`${selectedSala}`} disabled style={{ justifyContent: "flex-start", textAlign: "left" }}>
							Sala {selectedSala} (non disponibile)
						</MenuItem>
					)}
					<MenuItem value="0" style={{ justifyContent: "flex-start", textAlign: "left" }}>
						Nessuna Sala
					</MenuItem>
					{sale.map((sala) => (
						<MenuItem
							key={sala.sala}
							value={`${sala.sala}`}
							style={{ justifyContent: "flex-start", textAlign: "left" }}
						>
							{sala.label}
						</MenuItem>
					))}
				</TextField>
				<button
					type="button"
					className="activate-submit"
					onClick={openPopup}
					disabled={isExternal || loading}
					style={{ ...smallProcediButtonStyle, marginTop: 22 }}
				>
					Modifica Sale
				</button>
			</div>
			{isExternal && (
				<Typography variant="body2" className="camere-ardenti-permission-message">
					Per poter completare l'operazione sarà richiesto il permesso al proprietario della casa funeraria
				</Typography>
			)}
			{requestStatus === "pending" && (
				<Typography variant="body2" className="camere-ardenti-pending-message">
					Richiesta in attesa di autorizzazione da parte del proprietario.
				</Typography>
			)}

			<Dialog open={popupOpen} onClose={() => setPopupOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle className="camere-ardenti-dialog-title">Camere Ardenti: Modifica Sale</DialogTitle>
				<DialogContent>
					{popupError && <Typography variant="body2" className="camere-ardenti-error">{popupError}</Typography>}
					{popupLoading && <Typography variant="body2">Caricamento sale...</Typography>}
					{configSale.map((sala, index) => {
						const isLast = index === configSale.length - 1;
						return (
							<div className="camere-ardenti-config-row" key={sala.sala}>
								<span className="camere-ardenti-bullet">•</span>
								<Typography className="camere-ardenti-room-label">Sala</Typography>
								<TextField
									fullWidth
									placeholder={`${sala.sala} (Sovrascrivi per rinominare)`}
									value={sala.nomeSala || ""}
									onChange={(event) => updateConfigSalaName(index, event.target.value)}
									onBlur={() => renameSala(configSale[index])}
									disabled={popupSaving}
								/>
								{isLast && (
									<Button
										variant="contained"
										onClick={() => runConfigPost("camereArdentiDeleteLast", {})}
										className="camere-ardenti-delete-button"
									>
										<Delete />
									</Button>
								)}
							</div>
						);
					})}
					<Button
						fullWidth
						onClick={() => runConfigPost("camereArdentiAdd", {})}
						style={{ color: "#d49a00", justifyContent: "flex-start", marginTop: 6 }}
					>
						Aggiungi Sala
					</Button>
				</DialogContent>
				<DialogActions>
					<button
						type="button"
						className="activate-submit camere-ardenti-dialog-close"
						onClick={() => setPopupOpen(false)}
						style={closeProcediButtonStyle}
					>
						Chiudi
					</button>
				</DialogActions>
			</Dialog>
		</div>
	);
}
